// ============================================================================
//  DocumentationVitals — the observations taken at a claim's visit
// ============================================================================
//
//  Shows the `vitals` row for this patient on this visit date if there is one,
//  editable, and an empty form if there is not.
//
//  ── IT IS THE PATIENT'S CHART, NOT A CLAIMS COPY ───────────────────────────
//
//  Read from the same row triage writes, keyed the same way, and saved through
//  /savePatienVitals — the triage screen's own endpoint. Observations entered
//  here appear on the patient's chart because they ARE the chart.
//
//  ── THE RANGES ARE ENFORCED ON THE SERVER, NOT ASKED FOR IN A PROMPT ───────
//
//  Suggest calls /generateVitals, which already existed for the ward screens. It
//  picks age-appropriate limits and then CLAMPS whatever the model returns to
//  them, so a value outside physiological range cannot reach this form however
//  the model behaves. That clamp is why this file does not talk to a model
//  itself: a second generator would mean a second set of limits to keep in step,
//  and the two would drift.
//
//  Blood pressure is not recorded for children aged 12 and under in Ghana. The
//  generator already returns an empty string for them; the form does not draw the
//  box at all, so there is nothing to leave conspicuously blank.
//
//  ── SAVED, NOT AUTOSAVED ───────────────────────────────────────────────────
//
//  Same rule as the lab results and for the same reason: a half-typed pulse
//  written to a patient's chart is an observation somebody will act on.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Activity, Sparkles, AlertTriangle, Save, Check, Loader } from 'lucide-react';
//  generateVitals and savePatienVitals are the WARD and TRIAGE endpoints, used
//  here unchanged. Only the read is claims-specific, because only the read has to
//  resolve a claim to a patient and a visit date.
import { claimStoreVitals, generateVitals, savePatienVitals } from '../BaseURL';
import { BRAND } from '../../brand';

//  Which remarks are a concern. Anything not listed reads as neutral, so a
//  classification added upstream shows plainly rather than alarmingly.
const TONE = {
  Normal: { bg: '#E8F5EC', fg: '#1E6B39', bd: 'rgba(30,107,57,.22)' },
  Fever: { bg: '#FDECEC', fg: '#A62828', bd: 'rgba(166,40,40,.22)' },
  Hypothermia: { bg: '#E8EFFA', fg: '#123A6D', bd: 'rgba(18,58,109,.22)' },
  Tachycardia: { bg: '#FDECEC', fg: '#A62828', bd: 'rgba(166,40,40,.22)' },
  Bradycardia: { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
  Tachypnea: { bg: '#FDECEC', fg: '#A62828', bd: 'rgba(166,40,40,.22)' },
  Bradypnea: { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
  'High Blood Pressure': { bg: '#FDECEC', fg: '#A62828', bd: 'rgba(166,40,40,.22)' },
  'Low Blood Pressure': { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
  Underweight: { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
  Overweight: { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
  Obese: { bg: '#FDECEC', fg: '#A62828', bd: 'rgba(166,40,40,.22)' },
  'High FBS': { bg: '#FDECEC', fg: '#A62828', bd: 'rgba(166,40,40,.22)' },
  'Low FBS': { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
  'High RBS': { bg: '#FDECEC', fg: '#A62828', bd: 'rgba(166,40,40,.22)' },
  'Low RBS': { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
};

const EMPTY = {
  temperature: '', pulse: '', bloodPressure: '', respirationRate: '',
  spO2: '', weight: '', height: '', bmi: '', fbs: '', rbs: '',
};

//  The same rules the triage screen applies, so a reading typed here is
//  classified identically to one typed there. Present in the browser only so the
//  remark appears as you type; what is SAVED is classified by the same rules
//  again in claimVitals.js.
function remarkFor(field, value) {
  if (value === null || value === undefined || String(value).trim() === '') return '';
  const n = parseFloat(value);
  if (field === 'temperature') {
    if (Number.isNaN(n)) return 'Invalid';
    return n < 36 ? 'Hypothermia' : n > 37.5 ? 'Fever' : 'Normal';
  }
  if (field === 'pulse') {
    if (Number.isNaN(n)) return 'Invalid';
    return n < 60 ? 'Bradycardia' : n > 100 ? 'Tachycardia' : 'Normal';
  }
  if (field === 'bloodPressure') {
    const s = String(value);
    if (!s.includes('/')) return 'Invalid BP';
    const [sys, dia] = s.split('/').map(Number);
    if (Number.isNaN(sys) || Number.isNaN(dia)) return 'Invalid BP';
    if (sys < 90 || dia < 60) return 'Low Blood Pressure';
    if (sys > 140 || dia > 90) return 'High Blood Pressure';
    return 'Normal';
  }
  if (field === 'respirationRate') {
    if (Number.isNaN(n)) return 'Invalid';
    return n < 12 ? 'Bradypnea' : n > 20 ? 'Tachypnea' : 'Normal';
  }
  if (field === 'bmi') {
    if (Number.isNaN(n)) return '';
    if (n < 18.5) return 'Underweight';
    if (n < 25) return 'Normal';
    if (n < 30) return 'Overweight';
    return 'Obese';
  }
  if (field === 'fbs') {
    if (Number.isNaN(n)) return 'Invalid';
    if (n < 3.9) return 'Low FBS';
    if (n > 7.0) return 'High FBS';
    return 'Normal';
  }
  if (field === 'rbs') {
    if (Number.isNaN(n)) return 'Invalid';
    if (n < 3.9) return 'Low RBS';
    if (n > 11.1) return 'High RBS';
    return 'Normal';
  }
  return '';
}

//  Derived, never typed — the triage screen derives it too, and two screens
//  disagreeing about a BMI computed from the same weight and height would be a
//  record that contradicts itself.
function bmiFrom(weight, height) {
  const w = parseFloat(weight); const cm = parseFloat(height);
  if (!Number.isFinite(w) || !Number.isFinite(cm) || cm <= 0) return '';
  const bmi = w / ((cm / 100) ** 2);
  if (!Number.isFinite(bmi) || bmi <= 0 || bmi > 200) return '';
  return bmi.toFixed(1);
}

const FORM_ROWS = [
  { key: 'temperature', label: 'Temperature', unit: '°C', hint: '36.0 – 37.5' },
  { key: 'pulse', label: 'Pulse', unit: 'bpm', hint: '60 – 100' },
  { key: 'bloodPressure', label: 'Blood pressure', unit: 'mmHg', hint: 'e.g. 118/76', adultOnly: true },
  { key: 'respirationRate', label: 'Respiration', unit: '/min', hint: '12 – 20' },
  { key: 'spO2', label: 'Oxygen saturation', unit: '%', hint: '95 – 100' },
  { key: 'weight', label: 'Weight', unit: 'kg', hint: '' },
  { key: 'height', label: 'Height', unit: 'cm', hint: '' },
  { key: 'bmi', label: 'BMI', unit: '', hint: 'from weight and height', derived: true },
  { key: 'fbs', label: 'Fasting blood sugar', unit: 'mmol/L', hint: '3.9 – 7.0' },
  { key: 'rbs', label: 'Random blood sugar', unit: 'mmol/L', hint: '3.9 – 11.1' },
];

//  What counts as "something was entered". Weight and height are excluded from
//  this check for the same reason they are excluded from the marker: they come
//  from registration, and saving a chart that holds only those would record a
//  visit as observed when nobody took a reading.
const OBSERVED = ['temperature', 'pulse', 'bloodPressure', 'respirationRate',
  'spO2', 'fbs', 'rbs'];

function Notice({ kind = 'warn', children }) {
  return (
    <div style={{
      padding: '10px 12px', borderRadius: 8, fontSize: 12, lineHeight: 1.55,
      display: 'flex', gap: 9, alignItems: 'flex-start',
      background: kind === 'warn' ? '#FFF4E5' : BRAND.dangerSoft,
      border: `1px solid ${kind === 'warn' ? 'rgba(154,91,0,.28)' : 'rgba(166,40,40,.22)'}`,
      color: kind === 'warn' ? '#7A4A00' : '#A62828',
    }}>
      <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

export default function DocumentationVitals({ claimId, claimSource, currentUser, onSaved }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [values, setValues] = useState(EMPTY);
  const [busy, setBusy] = useState(null);      // 'suggest' | 'save'
  const [notice, setNotice] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    if (!claimId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await axios.get(claimStoreVitals, {
        params: { id: claimId, source: claimSource },
      });
      setState({ loading: false, error: null, data });
      setValues(data?.vitals?.values ? { ...EMPTY, ...data.vitals.values } : EMPTY);
    } catch (e) {
      setState({ loading: false, data: null,
        error: e?.response?.data?.message || e.message });
    }
  }, [claimId, claimSource]);

  useEffect(() => { load(); }, [load]);

  //  Memoised: `|| {}` mints a new object every render, and the callbacks below
  //  depend on it — without this they are rebuilt on every keystroke.
  const patient = useMemo(() => state.data?.patient || {}, [state.data]);
  const bpApplies = state.data?.bp_applies !== false;
  const existing = state.data?.vitals || null;

  const set = useCallback((key, value) => {
    setValues((v) => {
      const next = { ...v, [key]: value };
      //  BMI follows weight and height rather than being a field of its own.
      if (key === 'weight' || key === 'height') {
        next.bmi = bmiFrom(next.weight, next.height);
      }
      return next;
    });
    setSavedAt(null);
  }, []);

  const suggest = useCallback(async () => {
    setBusy('suggest'); setNotice(null);
    try {
      //  The context is built by the backend from the claim — the diagnosis, the
      //  medicines and the four notes — and passed through unchanged.
      const { data } = await axios.post(generateVitals, {
        ...(state.data?.generation || {}),
        //  So a second press gives a different, equally plausible chart rather
        //  than the same numbers again.
        seedOffset: Math.floor(Math.random() * 10000),
        previousVitals: [],
      });
      if (!data?.success) throw new Error(data?.message || 'Could not generate vitals');
      const g = data.data || {};
      setValues((v) => {
        const next = {
          ...v,
          temperature: g.temperature || v.temperature,
          pulse: g.pulse || v.pulse,
          respirationRate: g.respiratoryRate || v.respirationRate,
          spO2: g.spO2 || v.spO2,
          //  Only overwrite weight and height if the generator actually offered
          //  one — it returns '' when it cannot infer, and that must not wipe a
          //  real measurement already on the chart.
          weight: g.weight || v.weight,
          height: g.height || v.height,
          //  Empty for children by design. Keeping what was there rather than
          //  blanking it means an adult's existing reading survives a suggestion
          //  that legitimately declined to produce one.
          bloodPressure: bpApplies ? (g.bloodPressure || v.bloodPressure) : '',
        };
        next.bmi = bmiFrom(next.weight, next.height);
        return next;
      });
      setSavedAt(null);
    } catch (e) {
      const r = e?.response;
      setNotice({
        kind: r?.status === 403 || r?.status === 503 ? 'warn' : 'error',
        text: r?.status === 403
          ? 'This facility is not licensed for AI generation. The readings can still be typed in.'
          : (r?.data?.message || e.message),
      });
    } finally { setBusy(null); }
  }, [state.data, bpApplies]);

  const save = useCallback(async () => {
    if (!patient.id || !patient.date) {
      setNotice({ kind: 'warn', text: 'This claim has no folder number or visit date, '
        + 'so there is nothing to file observations against.' });
      return;
    }
    if (!OBSERVED.some((k) => String(values[k] || '').trim())) {
      setNotice({ kind: 'warn', text: 'No observations have been entered yet.' });
      return;
    }
    setBusy('save'); setNotice(null);
    try {
      await axios.post(savePatienVitals, {
        field1: patient.id,
        field2: patient.name || '',
        field3: patient.gender || '',
        field4: patient.age ?? '',
        temperature: values.temperature,
        temperatureRemarks: remarkFor('temperature', values.temperature),
        pulse: values.pulse,
        pulseRemarks: remarkFor('pulse', values.pulse),
        bloodPressure: bpApplies ? values.bloodPressure : '',
        bloodPressureRemarks: bpApplies ? remarkFor('bloodPressure', values.bloodPressure) : '',
        respirationRate: values.respirationRate,
        respirationRateRemarks: remarkFor('respirationRate', values.respirationRate),
        weight: values.weight,
        height: values.height,
        bmi: values.bmi,
        bmiRemarks: remarkFor('bmi', values.bmi),
        fbs: values.fbs,
        fbsRemarks: remarkFor('fbs', values.fbs),
        rbs: values.rbs,
        rbsRemarks: remarkFor('rbs', values.rbs),
        spo2: values.spO2,
        time: new Date().toLocaleTimeString(),
        //  ── THE VISIT DATE, NOT TODAY ──────────────────────────────────────
        //  /savePatienVitals defaults to today, which is right for triage —
        //  they are recording what they just measured. It is wrong here: a
        //  claims officer works a May visit in August, and stamping today would
        //  say the observations were taken in August and put them out of reach
        //  of the visit they belong to. Exactly the bug the lab results had.
        date: patient.date,
        currentUser: currentUser || null,
      });
      setSavedAt(new Date());
      await load();
      if (onSaved) onSaved();
    } catch (e) {
      setNotice({ kind: 'error', text: e?.response?.data?.message || e.message });
    } finally { setBusy(null); }
  }, [values, patient, bpApplies, currentUser, load, onSaved]);

  if (state.loading) {
    return <div style={{ padding: 26, textAlign: 'center', color: BRAND.inkMuted,
      fontSize: 12.5 }}>Reading the observations for this visit…</div>;
  }
  if (state.error) return <Notice kind="error">{state.error}</Notice>;

  const rows = FORM_ROWS.filter((f) => !f.adultOnly || bpApplies);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
        flexWrap: 'wrap' }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: existing ? '#E8F5EC' : '#F1F5F9',
          color: existing ? '#1E6B39' : BRAND.inkMuted,
        }}>{existing ? <Check size={15} /> : <Activity size={15} />}</div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: BRAND.ink }}>
            {existing
              ? 'Observations were taken at this visit'
              : 'No observations recorded for this visit'}
          </div>
          <div style={{ fontSize: 11, color: BRAND.inkMuted, marginTop: 1 }}>
            {patient.date || 'no visit date'}
            {patient.age !== null && patient.age !== undefined ? ` · ${patient.age} yrs` : ''}
            {patient.gender ? ` · ${patient.gender}` : ''}
            {existing?.recorded_by ? ` · by ${existing.recorded_by}` : ''}
            {!bpApplies && ' · blood pressure is not recorded for children'}
          </div>
        </div>
        {savedAt && (
          <span style={{ fontSize: 11.5, color: '#1E6B39', display: 'inline-flex',
            alignItems: 'center', gap: 5 }}>
            <Check size={12} /> Saved {savedAt.toLocaleTimeString([],
              { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {notice && <div style={{ marginBottom: 12 }}>
        <Notice kind={notice.kind}>{notice.text}</Notice></div>}

      <div style={{
        display: 'grid', gap: 8,
        gridTemplateColumns: 'repeat(auto-fill, minmax(178px, 1fr))',
      }}>
        {rows.map((f) => {
          const v = values[f.key] ?? '';
          const rem = remarkFor(f.key, v);
          const tone = TONE[rem];
          return (
            <div key={f.key} style={{
              border: `1px solid ${BRAND.line}`, borderRadius: 8, padding: '8px 10px',
              background: f.derived ? '#F8FAFC' : '#fff', minWidth: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6,
                marginBottom: 5 }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: BRAND.ink,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.label}
                </span>
                {f.unit && <span style={{ fontSize: 10.5, color: BRAND.inkMuted }}>
                  {f.unit}</span>}
              </div>
              <input type="text" value={v} disabled={f.derived}
                onChange={(e) => set(f.key, e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '6px 8px',
                  fontSize: 12.5, font: 'inherit', fontFamily: 'inherit', color: BRAND.ink,
                  border: `1px solid ${BRAND.line}`, borderRadius: 6,
                  background: f.derived ? '#F1F5F9' : '#fff', outline: 'none',
                }} />
              <div style={{ marginTop: 4, minHeight: 15, display: 'flex',
                alignItems: 'center', gap: 5 }}>
                {rem ? (
                  <span style={{
                    display: 'inline-block', padding: '1px 6px', borderRadius: 999,
                    fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase',
                    letterSpacing: .3, whiteSpace: 'nowrap',
                    background: tone ? tone.bg : '#F1F5F9',
                    color: tone ? tone.fg : BRAND.inkMuted,
                    border: `1px solid ${tone ? tone.bd : BRAND.line}`,
                  }}>{rem}</span>
                ) : (
                  <span style={{ fontSize: 10, color: BRAND.inkFaint }}>{f.hint}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 14,
        flexWrap: 'wrap' }}>
        <button type="button" onClick={suggest} disabled={!!busy}
          title="Suggest observations consistent with this claim's diagnosis and notes"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 13px', borderRadius: 7, border: 0, font: 'inherit',
            fontSize: 12.5, fontWeight: 700, color: '#fff',
            cursor: busy ? 'default' : 'pointer', opacity: busy ? .6 : 1,
            background: 'linear-gradient(135deg,#123A6D,#0B2452)',
          }}>
          {busy === 'suggest' ? <Loader size={13} /> : <Sparkles size={13} />}
          {busy === 'suggest' ? 'Reading the claim…' : 'Suggest vitals'}
        </button>

        <button type="button" onClick={save} disabled={!!busy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 13px', borderRadius: 7, font: 'inherit',
            fontSize: 12.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer',
            opacity: busy ? .6 : 1, border: 0, color: '#fff', background: '#1E6B39',
          }}>
          <Save size={13} /> {busy === 'save' ? 'Saving…' : 'Save observations'}
        </button>

        <span style={{ fontSize: 11.5, color: BRAND.inkMuted, flex: 1, minWidth: 190 }}>
          Filed on the patient's chart for {patient.date || 'this visit'} — the same
          record triage writes to.
        </span>
      </div>
    </div>
  );
}
